import { useCallback, useEffect, useRef, useState } from 'react';
import { Accordion } from './Accordion';
import { ContactRequestForm } from './ContactRequestForm';
import { Modal } from './Modal';
import {
  EDUCATIONAL_MATERIAL_TITLE,
  EducationalMaterial,
  PRIVACY_PRACTICES_TITLE,
  PrivacyPractices,
  VideoLightbox,
} from './popups';
import { content } from './content';
import { InfoField, RadioGroup, SectionHeading, SelectField, TextField } from './fields';
import { SignaturePad } from './SignaturePad';
import { ThankYou } from './ThankYou';
import { RELATIONSHIP_OPTIONS } from './types';
import { formatPhone, validate } from './validation';
const EMPTY = {
  patientFirstName: '',
  patientLastName: '',
  patientDateOfBirth: '',
  completedBy: 'proxy',
  // pre-selected on the live site
  proxyFirstName: '',
  proxyLastName: '',
  proxyEmail: '',
  proxyPhone: '',
  proxyRelationship: RELATIONSHIP_OPTIONS[0],
  consentChoice: 'opt_in',
  // pre-selected on the live site
  signatureDataUrl: '',
  copyEmail: '',
};
function ConsentForm({ facility, onSubmit, onContactRequest, links = {},onSuccess, initialValues, renderThankYou, className, checkProxyPhone }) {
  const [values, setValues] = useState({
    ...EMPTY,
    ...initialValues,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(null);
  const [popup, setPopup] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const formRef = useRef(null);
  const [proxyLineCheck, setProxyLineCheck] = useState({ status: 'idle', response: null, number: '', message: '' });
  useEffect(() => {
    const number = String(values.proxyPhone || '').replace(/\D/g, '');
    if (values.completedBy !== 'proxy' || !number) {
      setProxyLineCheck({ status: 'idle', response: null, number: '', message: '' });
      return;
    }
    if (number.length !== 10) {
      setProxyLineCheck({ status: 'bad', response: null, number, message: 'Enter a 10-digit phone number.' });
      return;
    }
    if (!checkProxyPhone) return;
    const controller = new AbortController();
    setProxyLineCheck({ status: 'checking', response: null, number, message: 'Checking phone line type…' });
    const timer = setTimeout(async () => {
      try {
        const response = await checkProxyPhone(number, controller.signal);
        if (!controller.signal.aborted) setProxyLineCheck({
          status: response?.valid === true ? 'ok' : 'bad',
          response, number,
          message: response?.valid === true ? 'Phone number verified.' : 'Phone number is not valid.',
        });
      } catch (error) {
        if (!controller.signal.aborted) setProxyLineCheck({
          status: 'error', response: null, number,
          message: error?.message || 'Unable to verify phone number.',
        });
      }
    }, 900);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [values.completedBy, values.proxyPhone, checkProxyPhone]);
  const set = useCallback(
    (key, value) => {
      setValues((v) => {
        const next = { ...v, [key]: value };
        if (submitted) setErrors(validate(next));
        return next;
      });
    },
    [submitted],
  );
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setSubmitError(null);
    const errs = validate(values);
    setErrors(errs);
    if (values.completedBy === 'proxy' && checkProxyPhone &&
      (proxyLineCheck.status !== 'ok' ||
       proxyLineCheck.response?.valid !== true ||
       proxyLineCheck.number !== String(values.proxyPhone).replace(/\D/g, ''))) {
      errs.proxyPhone = 'A valid, verified proxy phone number is required.';
      setErrors({ ...errs });
    }
    if (Object.keys(errs).length > 0) {
      requestAnimationFrame(() => {
        const el = formRef.current?.querySelector(
          '.tcf-field--error input, .tcf-field--error select, .tcf-signature--error',
        );
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.focus({ preventScroll: true });
      });
      return;
    }
    const submission = {
      facility,
      patient: {
        firstName: values.patientFirstName.trim(),
        lastName: values.patientLastName.trim(),
        dateOfBirth: values.patientDateOfBirth,
      },
      completedBy: values.completedBy,
      proxy:
        values.completedBy === 'proxy'
          ? {
              firstName: values.proxyFirstName.trim(),
              lastName: values.proxyLastName.trim(),
              email: values.proxyEmail.trim(),
              phone: values.proxyPhone,
              relationship: values.proxyRelationship,
            }
          : null,
      consentChoice: values.consentChoice,
      signatureDataUrl: values.signatureDataUrl,
      copyEmail: values.copyEmail.trim(),
      proxyLineCheck: { ...proxyLineCheck, valid: proxyLineCheck.response?.valid === true },
      submittedAt: /* @__PURE__ */ new Date().toISOString(),
    };
    try {
      setSubmitting(true);
      const submitResult = await onSubmit(submission);
      setDone({ ...submission, pdfFile: submitResult?.pdfFile || null });
      onSuccess?.();
    } catch (err) {
      setSubmitError(err instanceof Error && err.message ? err.message : content.errors.submitFailed);
    } finally {
      setSubmitting(false);
    }
  };
  if (done) {
    return (
      <div className="tcf-root">
        <div className={['tcf', className ?? ''].join(' ').trim()}>
          {renderThankYou ? renderThankYou(done) : <ThankYou submission={done} />}
        </div>
      </div>
    );
  }
  const c = content;
  const signatureHeadingId = 'tcf-signature-heading';
  return (
    // .tcf-root is a CSS container: the layout adapts to the width the form is given, not only to the screen
    <div className="tcf-root">


      <form ref={formRef} className={['tcf', className ?? ''].join(' ').trim()} onSubmit={handleSubmit} noValidate>


        {/* ---------- Patient ---------- */}
        <section className="tcf-section" aria-labelledby="tcf-patient-heading">
          <SectionHeading id="tcf-patient-heading" title={c.patient.heading} description={c.patient.description} />
          <div className="tcf-row">
            <TextField
              label={c.patient.labels.firstName}
              required
              name="patientFirstName"
              autoComplete="given-name"
              value={values.patientFirstName}
              onChange={(e) => set('patientFirstName', e.target.value)}
              error={errors.patientFirstName}
            />
            <TextField
              label={c.patient.labels.lastName}
              required
              name="patientLastName"
              autoComplete="family-name"
              value={values.patientLastName}
              onChange={(e) => set('patientLastName', e.target.value)}
              error={errors.patientLastName}
            />
          </div>
          <div className="tcf-row">
            <TextField
              label={c.patient.labels.dateOfBirth}
              required
              type="date"
              name="patientDateOfBirth"
              autoComplete="bday"
              max={/* @__PURE__ */ new Date().toISOString().slice(0, 10)}
              value={values.patientDateOfBirth}
              onChange={(e) => set('patientDateOfBirth', e.target.value)}
              error={errors.patientDateOfBirth}
            />
          </div>
        </section>

        {/* ---------- Who is completing this ---------- */}
        <section className="tcf-section" aria-labelledby="tcf-completedby-heading">
          <SectionHeading
            id="tcf-completedby-heading"
            title={c.completedBy.heading}
            description={c.completedBy.description}
          />
          <RadioGroup
            name="completedBy"
            legend={c.completedBy.heading}
            legendHidden
            required
            value={values.completedBy}
            onChange={(v) => set('completedBy', v)}
            error={errors.completedBy}
            options={[
              { value: 'patient', label: c.completedBy.options.patient },
              { value: 'proxy', label: c.completedBy.options.proxy },
            ]}
          />
          {values.completedBy === 'proxy' && (
            <div className="tcf-proxy">
              <div className="tcf-row">
                <TextField
                  label={c.completedBy.proxyLabels.firstName}
                  required
                  name="proxyFirstName"
                  value={values.proxyFirstName}
                  onChange={(e) => set('proxyFirstName', e.target.value)}
                  error={errors.proxyFirstName}
                />
                <TextField
                  label={c.completedBy.proxyLabels.lastName}
                  required
                  name="proxyLastName"
                  value={values.proxyLastName}
                  onChange={(e) => set('proxyLastName', e.target.value)}
                  error={errors.proxyLastName}
                />
              </div>
              <div className="tcf-row">
                <TextField
                  label={c.completedBy.proxyLabels.email}
                  required
                  type="email"
                  name="proxyEmail"
                  autoComplete="email"
                  inputMode="email"
                  value={values.proxyEmail}
                  onChange={(e) => set('proxyEmail', e.target.value)}
                  error={errors.proxyEmail}
                />
                <TextField
                  label={c.completedBy.proxyLabels.phone}
                  required
                  type="tel"
                  name="proxyPhone"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="(999) 999-9999"
                  value={values.proxyPhone}
                  onChange={(e) => set('proxyPhone', formatPhone(e.target.value))}
                  error={errors.proxyPhone}
                />
                {values.proxyPhone && checkProxyPhone && (
                  <p className={proxyLineCheck.status === 'error' || proxyLineCheck.status === 'bad' ? 'tcf-error' : 'tcf-section-desc'}
                     role="status">{proxyLineCheck.message}</p>
                )}
              </div>
              <SelectField
                label={c.completedBy.proxyLabels.relationship}
                required
                name="proxyRelationship"
                options={RELATIONSHIP_OPTIONS}
                value={values.proxyRelationship}
                onChange={(e) => set('proxyRelationship', e.target.value)}
                error={errors.proxyRelationship}
              />
            </div>
          )}
        </section>

        {/* ---------- Consent choice ---------- */}
        <section className="tcf-section" aria-labelledby="tcf-choice-heading">
          <SectionHeading
              id="tcf-choice-heading"
              title={`Agree to enroll in advanced monitoring support at ${facility.name}`}
          />
          <RadioGroup
            name="consentChoice"
            legend={c.consentChoice.legend}
            legendHidden
            required
            value={values.consentChoice}
            onChange={(v) => set('consentChoice', v)}
            error={errors.consentChoice}
            options={[
              { value: 'opt_in', label: c.consentChoice.options.opt_in },
              { value: 'opt_out', label: c.consentChoice.options.opt_out },
            ]}
          />
        </section>

        {/* ---------- Consent agreement (collapsible) ---------- */}
        <Accordion title={c.agreement.heading}>
          <div className="tcf-legal-box">
            <div className="tcf-legal">
              <p>{c.agreement.intro}</p>
              <ul>
                {c.agreement.understand.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              <p>{c.agreement.bySigning}</p>
              <ul>
                {c.agreement.acknowledgements.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ul>
              <h4 className="tcf-eyebrow tcf-legal-subhead">{c.agreement.rightsHeading}</h4>
              <ul>
                <li>{c.agreement.rights[0]}</li>
                <li>
                  {c.agreement.rights[1]}
                  <a href={`mailto:${c.agreement.rightsEmail}`}>{c.agreement.rightsEmail}</a>.
                </li>
              </ul>
            </div>
          </div>
          {links.consentPdfHref && (
          <a
            className="tcf-link--pdf"
            href={links.consentPdfHref ?? '#'}
            target={links.consentPdfHref ? '_blank' : void 0}
            rel="noreferrer"
          >
            {c.agreement.downloadPdf}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M3 1H2C1.44772 1 1 1.44772 1 2V12C1 12.5523 1.44772 13 2 13H12C12.5523 13 13 12.5523 13 12V11"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
              <path
                d="M5 9L13 1M13 1H8.5M13 1V5.5"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
          )}
        </Accordion>

        {/* ---------- Before you sign (collapsible, like the live site) ---------- */}
        <Accordion title={c.beforeYouSign.heading}>
          <div className="tcf-before">
            <p className="tcf-before-lead">{c.beforeYouSign.lead}</p>
            <p className="tcf-before-body">{c.beforeYouSign.body}</p>
            <div className="tcf-before-links">
              <button
                type="button"
                className="tcf-link tcf-link--teal tcf-link-button"
                onClick={links.onEducationalMaterial ?? (() => setPopup('education'))}
              >
                {c.beforeYouSign.educationalMaterial}
              </button>
              <button
                type="button"
                className="tcf-link tcf-link--orange tcf-link-button"
                onClick={links.onPrivacyPractices ?? (() => setPopup('privacy'))}
              >
                {c.beforeYouSign.privacyPractices}
              </button>
            </div>
          </div>
        </Accordion>

        {/* ---------- Facility information (read-only; Provider # is hidden on the live site) ---------- */}
        <section className="tcf-section tcf-facility" aria-labelledby="tcf-facility-heading">
          <SectionHeading
            id="tcf-facility-heading"
            title={c.facility.heading}
            description={
              <>
                {onContactRequest && c.facility.descriptionBefore}
                {onContactRequest && <button
                  type="button"
                  className="tcf-link tcf-link--teal tcf-link-button tcf-link-button--inline"
                  onClick={links.onFacilityContact ?? (() => setPopup('contact'))}
                >
                  {c.facility.descriptionLink}
                </button>}
                {onContactRequest && c.facility.descriptionAfter}
              </>
            }
          />
          <div className="tcf-facility-group">
            <InfoField label={c.facility.labels.name} value={`${facility.name} (${facility.providerNumber})`} />
            <InfoField label={c.facility.labels.address} value={facility.address} />
          </div>
        </section>

        {/* ---------- Signature ---------- */}
        <section className="tcf-section" aria-labelledby={signatureHeadingId}>
          <h3 className="tcf-eyebrow tcf-eyebrow--required" id={signatureHeadingId}>
            {c.signature.heading}
            <span className="tcf-required" aria-hidden="true">
              *
            </span>
          </h3>
          <SignaturePad
            labelledBy={signatureHeadingId}
            clearLabel={c.signature.clear}
            onChange={(d) => set('signatureDataUrl', d)}
            error={errors.signatureDataUrl}
          />
        </section>

        {/* Signed-copy email entry is hidden until an actual email-delivery route exists. */}

        {/* ---------- Submit ---------- */}
        <div className="tcf-footer">
          {submitError && (
            <p className="tcf-error tcf-error--submit" role="alert">
              {submitError}
            </p>
          )}
          <button type="submit" className="tcf-submit" disabled={submitting}>
            {submitting ? c.submit.submitting : c.submit.button}
          </button>
          <p className="tcf-submit-note">{c.submit.note}</p>
        </div>

        {/* ---------- Popups ---------- */}
        <Modal open={popup === 'education'} onClose={() => setPopup(null)} title={EDUCATIONAL_MATERIAL_TITLE}>
          <EducationalMaterial facilityName={facility.name} onPlayVideo={() => setVideoOpen(true)} />
        </Modal>
        <Modal open={popup === 'privacy'} onClose={() => setPopup(null)} title={PRIVACY_PRACTICES_TITLE}>
          <PrivacyPractices />
        </Modal>
        <Modal
          open={popup === 'contact'}
          onClose={() => setPopup(null)}
          title={content.contactRequest.title}
          titleHidden
        >
          <ContactRequestForm variant="facility" onSubmit={onContactRequest ?? (async () => void 0)} />
        </Modal>
        <VideoLightbox open={videoOpen} onClose={() => setVideoOpen(false)} />
      </form>
    </div>
  );
}
export { ConsentForm };
